import React from 'react';
import { Mail, Lock, Eye, EyeOff, Shield, CheckCircle } from 'lucide-react';

const LoginPage = ({ 
  showSignup, 
  setShowSignup, 
  loginForm, 
  setLoginForm, 
  signupForm, 
  setSignupForm, 
  handleLogin, 
  handleSignup, 
  error, 
  setError,
  onBrowseWithoutAccount,
  // Enhanced auth props
  authMode,
  setAuthMode,
  emailForPasswordless,
  setEmailForPasswordless,
  resetEmailSent,
  emailLinkSent,
  emailVerificationSent,
  handlePasswordlessLogin,
  handlePasswordReset,
  handleSignupWithVerification,
  resendEmailVerification,
  currentUser,
  handleTabChange,
  darkMode = true // Default to dark mode for login
}) => {
  const [showPassword, setShowPassword] = React.useState(false);

  return (
  <div className={`min-h-screen ${darkMode ? 'bg-black' : 'bg-white'} flex items-center justify-center p-4`}>
    <div className="w-full max-w-md">
      <div className="text-center mb-4 md:mb-8">
        <img 
          src="/newlogo.gif" 
          alt="TopBlast" 
          className="h-32 sm:h-48 md:h-64 lg:h-80 w-auto mx-auto mb-2 md:mb-4"
        />
        <h1 className={`text-xl sm:text-2xl md:text-3xl font-bold ${darkMode ? 'text-white' : 'text-black'} mb-1 md:mb-2`}>
          {authMode === 'signup' ? 'Join TopBlast' : 'Welcome back to'}
          <span className="block sm:inline sm:ml-2">{authMode === 'signup' ? '' : 'TopBlast'}</span>
        </h1>
        <p className={`text-sm sm:text-base ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>
          {authMode === 'signup' ? 'The pump.fun early detection platform' : 'Log in to discover explosions before they trend'}
        </p>
      </div>

      <div className="bg-gray-900 rounded-2xl p-4 sm:p-6 md:p-8">
        {error && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${
            error.startsWith('✅') 
              ? 'bg-green-900/20 border border-green-600 text-green-300' 
              : 'bg-red-900/20 border border-red-800 text-red-400'
          }`}>
            <div className="flex items-start gap-2">
              {error.startsWith('✅') ? (
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              ) : (
                <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <div>{error}</div>
                {error.includes('verify your email') && !error.startsWith('✅') && (
                  <button
                    onClick={resendEmailVerification}
                    className="text-yellow-400 hover:text-yellow-300 text-xs underline mt-2"
                  >
                    Resend verification email
                  </button>
                )}
              </div>
            </div>
          </div>
        )}


        {/* Note: Success messages for email verification are now handled via the error state in App.js */}

        {emailLinkSent && (
          <div className="mb-4 p-3 bg-green-900/20 border border-green-600 rounded-lg text-green-300 text-sm">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span>Sign-in link sent! Check your email and click the link.</span>
            </div>
          </div>
        )}

        {resetEmailSent && (
          <div className="mb-4 p-3 bg-green-900/20 border border-green-600 rounded-lg text-green-300 text-sm">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>Password reset email sent! Check your inbox.</span>
            </div>
          </div>
        )}

        {authMode === 'signup' ? (
          /* Sign Up Form */
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Username
              </label>
              <input
                type="text"
                value={signupForm.username}
                onChange={(e) => setSignupForm({...signupForm, username: e.target.value})}
                className="w-full bg-black border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                placeholder="basedanon"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  value={signupForm.email}
                  onChange={(e) => setSignupForm({...signupForm, email: e.target.value})}
                  className="w-full bg-black border border-gray-700 rounded-lg pl-10 pr-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                  placeholder="degen@moon.com"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={signupForm.password}
                  onChange={(e) => setSignupForm({...signupForm, password: e.target.value})}
                  className="w-full bg-black border border-gray-700 rounded-lg pl-10 pr-12 py-3 text-white focus:border-purple-500 focus:outline-none"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Gender
              </label>
              <div className="flex space-x-4">
                <button
                  onClick={() => setSignupForm({...signupForm, gender: 'male'})}
                  className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                    signupForm.gender === 'male' 
                      ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white' 
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  Male ♂
                </button>
                <button
                  onClick={() => setSignupForm({...signupForm, gender: 'female'})}
                  className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                    signupForm.gender === 'female' 
                      ? 'bg-pink-600 text-white' 
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  Female ♀
                </button>
              </div>
              {signupForm.gender === '' && (
                <p className="text-xs text-gray-500 mt-2">Select your biological gender</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Display Preference
              </label>
              <div className="flex space-x-4">
                <button
                  onClick={() => setSignupForm({...signupForm, displayPreference: 'marketcap'})}
                  className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                    signupForm.displayPreference === 'marketcap' 
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' 
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  📊 Market Cap
                </button>
                <button
                  onClick={() => setSignupForm({...signupForm, displayPreference: 'price'})}
                  className={`flex-1 py-3 rounded-lg font-medium transition-all ${
                    signupForm.displayPreference === 'price' 
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white' 
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  💵 Token Price
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Choose how you want to see token values throughout the app
              </p>
            </div>

            <button
              onClick={handleSignupWithVerification}
              className="w-full mt-6 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-bold py-3 rounded-lg transition-all transform hover:scale-105"
            >
              Create Account
            </button>
            
            <div className="bg-green-900/20 border border-green-600 rounded-lg p-3">
              <p className="text-green-300 text-xs">
                🚀 Your account will be ready to use immediately after signup - no email verification required!
              </p>
            </div>

            <div className="mt-6 text-center">
              <span className="text-gray-500">Already have a TopBlast account? </span>
              <button
                onClick={() => setAuthMode('login')}
                className="text-blue-400 hover:text-blue-300 font-medium"
              >
                Sign in
              </button>
            </div>
          </div>
        ) : (
          /* Login Form */
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                  onKeyPress={(e) => e.key === 'Enter' && (authMode === 'passwordless' ? handlePasswordlessLogin() : handleLogin())}
                  className="w-full bg-black border border-gray-700 rounded-lg pl-10 pr-4 py-3 text-white focus:border-purple-500 focus:outline-none"
                  placeholder="based@degen.com"
                  required
                />
              </div>
            </div>

            {/* Auth method selector with title */}
            <div className="mt-4 pt-4 border-t border-gray-800">
              <p className="text-center text-sm text-gray-400 mb-3">Choose your login method</p>
              <div className="flex bg-gray-900 rounded-lg p-1 border border-gray-700">
                <button
                  onClick={() => setAuthMode('login')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                    authMode === 'login'
                      ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  Password
                </button>
                <button
                  onClick={() => setAuthMode('passwordless')}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                    authMode === 'passwordless'
                      ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  Email Link
                </button>
              </div>
            </div>

            {authMode === 'login' && (
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                    onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                    className="w-full bg-black border border-gray-700 rounded-lg pl-10 pr-12 py-3 text-white focus:border-purple-500 focus:outline-none"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}

            {authMode === 'passwordless' && (
              <p className="text-xs text-gray-500">We'll send you a secure sign-in link</p>
            )}

            <button
              onClick={authMode === 'passwordless' ? handlePasswordlessLogin : handleLogin}
              disabled={authMode === 'passwordless' && emailLinkSent}
              className="w-full mt-6 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-bold py-3 rounded-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {authMode === 'passwordless' 
                ? (emailLinkSent ? 'Email Sent! Check Inbox' : 'Send Sign-In Link')
                : 'Sign In'
              }
            </button>

            {authMode === 'login' && (
              <div className="text-center">
                <button
                  onClick={handlePasswordReset}
                  className="text-purple-400 hover:text-purple-300 text-sm"
                >
                  Forgot password?
                </button>
              </div>
            )}

            <div className="mt-6 text-center">
              <span className="text-gray-500">Don't have a TopBlast account yet? </span>
              <button
                onClick={() => setAuthMode('signup')}
                className="text-blue-400 hover:text-blue-300 font-medium"
              >
                Sign up
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-gray-800">
          <button
            onClick={onBrowseWithoutAccount}
            className="w-full mb-4 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium py-3 rounded-lg transition-all"
          >
            Browse without account
          </button>
          <p className="text-center text-sm text-gray-500 mb-2">
            By continuing, you agree to everything in our{' '}
            <button
              onClick={() => {
                onBrowseWithoutAccount();
                // Small delay to ensure browse mode is set before changing tab
                setTimeout(() => {
                  handleTabChange('terms');
                }, 100);
              }}
              className="text-blue-400 hover:text-blue-300 underline"
            >
              Legal Shit
            </button>
            {' '}section 📜
          </p>
          <p className="text-center text-xs text-gray-600">
            TopBlast: Social Crypto Trading Platform - Rank-based prediction gaming
          </p>
        </div>
      </div>

      <div className="mt-4 sm:mt-8 text-center">
        <a 
          href="https://pump.fun/CkeUdo51AsXTtJgmVfhk1NNcBAdvdYwkPyqygUumpump" 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-purple-500 hover:text-purple-400 text-xs sm:text-sm block mb-4"
        >
          Don't have TopBlast tokens yet? Get some on pump.fun →
        </a>
      </div>
    </div>
  </div>
  );
};

export default LoginPage;